import { useEffect, useMemo, useState } from "react";
import {
  api,
  type LiveListing,
  type SavedSearch,
  type SavedSearchSelector,
  type ScanRun,
} from "../lib/api";
import { useWS } from "../lib/ws";
import ListingRow from "../components/ListingRow";

// How often the scheduler ticks. Mirrors the masterTickMs in
// src/server.ts. Used purely for the "next scan in X" countdown.
const SCAN_INTERVAL_MIN = 15;

type StatusFilter = "all" | "priced" | "pricing" | "no_comps";
type SortKey = "time_left" | "deal" | "bid" | "resale_floor" | "sold_median";

type ScanFeedback =
  | { kind: "starting" }
  | { kind: "fetched"; ebay: number; endingSoon: number; toPrice: number }
  | { kind: "finished"; live: number; repriced: number; status: string }
  | { kind: "skipped" };

export default function Dashboard() {
  const [searches, setSearches] = useState<SavedSearch[]>([]);
  // `selected` carries either a saved-search id or the literal "all"
  // sentinel for the cross-search aggregate view.
  const [selected, setSelected] = useState<SavedSearchSelector | null>(null);
  const [listings, setListings] = useState<LiveListing[]>([]);
  const [pulsedIds, setPulsedIds] = useState<Set<number>>(new Set());
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("priced");
  const [showEnded, setShowEnded] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>("deal");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [scanFeedback, setScanFeedback] = useState<ScanFeedback | null>(null);
  const [scanning, setScanning] = useState(false);
  const [lastRun, setLastRun] = useState<ScanRun | null>(null);
  const [now, setNow] = useState(() => Date.now());

  const selectedSearch = useMemo(
    () =>
      typeof selected === "number"
        ? (searches.find((s) => s.id === selected) ?? null)
        : null,
    [searches, selected],
  );

  // O(1) lookup of saved-search-by-id used in the "All searches" view —
  // each row's pulse threshold and per-search name come from here.
  const searchById = useMemo(() => {
    const m = new Map<number, SavedSearch>();
    for (const s of searches) m.set(s.id, s);
    return m;
  }, [searches]);

  const isAll = selected === "all";

  const visibleListings = useMemo(() => {
    // Recompute against the current wall clock so listings that ended
    // since the last fetch fall out of the default view immediately.
    const cutoffNow = Date.now();
    const filtered = listings.filter((l) => {
      if (!showEnded && new Date(l.end_time).getTime() <= cutoffNow) return false;
      if (statusFilter === "all") return true;
      return l.status === statusFilter;
    });
    const dir = sortDir === "asc" ? 1 : -1;
    const cmp = (a: LiveListing, b: LiveListing): number => {
      const get = (l: LiveListing): number | string => {
        if (sortKey === "time_left") return l.end_time;
        if (sortKey === "deal") return l.deal_score ?? -Infinity;
        if (sortKey === "bid") return l.total_cents;
        if (sortKey === "resale_floor") return l.resale_floor_cents ?? -Infinity;
        if (sortKey === "sold_median") return l.sold_median_cents ?? -Infinity;
        return 0;
      };
      const av = get(a);
      const bv = get(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    };
    return [...filtered].sort(cmp);
  }, [listings, statusFilter, sortKey, sortDir, showEnded]);

  useEffect(() => {
    void api.listSavedSearches().then((out) => {
      setSearches(out);
      if (selected === null) setSelected("all");
    });
  }, []);

  useEffect(() => {
    if (selected === null) return;
    const sel = selected;
    void api.listListings(sel).then(setListings);
    void api.listScanRuns(sel, 1).then((rs) => setLastRun(rs[0] ?? null));
    // Belt-and-suspenders polling fallback. WebSocket is the primary refresh
    // mechanism but we may miss events if the connection drops or the scan
    // started before we mounted; re-fetch every 5s while the dashboard is open.
    const t = setInterval(() => {
      void api.listListings(sel).then(setListings);
      void api.listScanRuns(sel, 1).then((rs) => setLastRun(rs[0] ?? null));
    }, 5000);
    // Tick every second so the "next scan in" countdown stays live.
    const tickClock = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      clearInterval(t);
      clearInterval(tickClock);
    };
  }, [selected]);

  useWS({
    savedSearchId: selected ?? "all",
    enabled: selected !== null,
    onEvent: (e) => {
      if (e.type === "listing.update") {
        // Re-fetch the full row from the server (simpler than reconciling pricing here)
        if (selected !== null) void api.listListings(selected).then(setListings);
      }
      if (e.type === "alert.new") {
        const data = e.data as { live_listing_id: number };
        setPulsedIds((prev) => new Set(prev).add(data.live_listing_id));
        setTimeout(() => {
          setPulsedIds((prev) => {
            const next = new Set(prev);
            next.delete(data.live_listing_id);
            return next;
          });
        }, 4_000);
      }
      if (e.type === "scan.start") {
        const data = e.data as {
          phase?: string;
          ebay_returned?: number;
          ending_soon?: number;
          to_price?: number;
        };
        if (data.phase === "fetched") {
          setScanFeedback({
            kind: "fetched",
            ebay: data.ebay_returned ?? 0,
            endingSoon: data.ending_soon ?? 0,
            toPrice: data.to_price ?? 0,
          });
        }
        setScanning(true);
      }
      if (e.type === "scan.finish") {
        const data = e.data as {
          status: string;
          live_count: number;
          repriced_count: number;
        };
        setScanning(false);
        setScanFeedback({
          kind: "finished",
          live: data.live_count,
          repriced: data.repriced_count,
          status: data.status,
        });
        // Auto-clear "finished" toast after 8s.
        setTimeout(() => setScanFeedback(null), 8_000);
      }
    },
  });

  async function handleScanNow() {
    if (selected === null) return;
    const sel = selected;
    // Force-flash the spinner state synchronously so the click always
    // produces a visible response — even if the backend returns in
    // <50ms the user still sees the "Scanning…" badge bounce.
    setScanning(true);
    setScanFeedback({ kind: "starting" });
    const clickedAt = Date.now();
    try {
      await api.scanNow(sel);
    } catch (e) {
      setScanFeedback(null);
      setScanning(false);
      throw e;
    }
    // Guarantee a minimum 800ms of "starting" visibility so it can't
    // flash through too fast to read when the scan kicks off quickly
    // (or when we hit the inflight guard).
    const elapsed = Date.now() - clickedAt;
    const minDelay = Math.max(0, 800 - elapsed);
    setTimeout(() => {
      // After 5s with no scan.start event from the server, assume the
      // request hit the inflight guard and update the badge.
      setScanFeedback((prev) =>
        prev?.kind === "starting" ? { kind: "skipped" } : prev,
      );
    }, minDelay + 5_000);
  }

  return (
    <div className="px-4 py-4 space-y-4">
      {/* flex-wrap, not flex: on a phone this row is far wider than the screen, and without
          it the whole page scrolled sideways rather than the row stacking. */}
      <header className="flex flex-wrap items-center gap-3">
        <select
          value={selected === null ? "" : String(selected)}
          onChange={(e) => {
            const v = e.target.value;
            setSelected(v === "all" ? "all" : Number(v));
          }}
          className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1 font-mono text-sm"
        >
          <option value="all">All searches</option>
          {searches.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
        <button
          onClick={() => void handleScanNow()}
          disabled={scanning}
          className={`px-3 py-1 rounded font-mono text-sm transition-colors disabled:cursor-not-allowed ${
            scanning
              ? "bg-yellow-500/20 text-yellow-300 border border-yellow-500/40"
              : "bg-zinc-800 hover:bg-zinc-700 text-zinc-100"
          }`}
        >
          {scanning ? (
            <span className="flex items-center gap-2">
              <Spinner />
              Scanning…
            </span>
          ) : (
            "Scan now"
          )}
        </button>
        <ScanScheduleIndicator lastRun={lastRun} now={now} />
        <ScanFeedbackBadge feedback={scanFeedback} />
        <ScanProgress listings={listings} />
      </header>

      {/* Measured at 712px on a 375px iPhone — the single worst offender in the app, and the
          reason the dashboard scrolled sideways. The controls now wrap into readable rows:
          the filter buttons wrap among themselves, and sort/select/direction stay together as
          one unit so the arrow never orphans onto its own line. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 font-mono text-xs">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
        <span className="text-zinc-500">filter:</span>
        {(["priced", "all", "pricing", "no_comps"] as const).map((s) => {
          // Counts respect the show-ended toggle so the badges match
          // what the user sees in the table.
          const cutoffNow = Date.now();
          const pool = showEnded
            ? listings
            : listings.filter((l) => new Date(l.end_time).getTime() > cutoffNow);
          const count =
            s === "all"
              ? pool.length
              : pool.filter((l) => l.status === s).length;
          const active = statusFilter === s;
          return (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-2 py-0.5 rounded ${active ? "bg-zinc-700 text-zinc-100" : "text-zinc-500 hover:text-zinc-300"}`}
            >
              {s} ({count})
            </button>
          );
        })}
        </div>
        <span className="text-zinc-700 hidden sm:inline">|</span>
        <div className="flex items-center gap-2 min-w-0">
        <span className="text-zinc-500">sort:</span>
        <select
          value={sortKey}
          onChange={(e) => setSortKey(e.target.value as SortKey)}
          className="bg-zinc-900 border border-zinc-800 rounded px-2 py-0.5 min-w-0"
        >
          <option value="deal">deal score</option>
          <option value="time_left">time left</option>
          <option value="bid">bid</option>
          <option value="resale_floor">resale floor</option>
          <option value="sold_median">sold median</option>
        </select>
        <button
          onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
          className="px-2 py-0.5 rounded text-zinc-400 hover:text-zinc-100"
          title="toggle sort direction"
        >
          {sortDir === "desc" ? "↓" : "↑"}
        </button>
        </div>
        <label className="flex items-center gap-1 text-zinc-400 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showEnded}
            onChange={(e) => setShowEnded(e.target.checked)}
            className="accent-zinc-500"
          />
          show ended
        </label>
        <span className="text-zinc-500 ml-auto">{visibleListings.length} of {listings.length}</span>
      </div>

      {(selectedSearch || isAll) && (
        <div className="overflow-x-auto border border-zinc-800 rounded">
          <table className="min-w-full">
            <thead className="text-xs text-zinc-500 font-mono">
              <tr>
                <th className="px-3 py-2 text-left">time left</th>
                {isAll && <th className="px-3 py-2 text-left">search</th>}
                <th className="px-3 py-2 text-left">title</th>
                <th className="px-3 py-2 text-right">bid</th>
                <th className="px-3 py-2 text-right">resale floor</th>
                <th className="px-3 py-2 text-right">sold floor</th>
                <th className="px-3 py-2 text-right">sold median</th>
                <th className="px-3 py-2 text-right">bin floor</th>
                <th className="px-3 py-2">deal</th>
                <th className="px-3 py-2">sold/bin</th>
                <th className="px-3 py-2">status</th>
              </tr>
            </thead>
            <tbody>
              {visibleListings.map((l) => {
                const ss = selectedSearch ?? searchById.get(l.saved_search_id);
                return (
                  <ListingRow
                    key={l.id}
                    listing={l}
                    alertThreshold={ss?.alert_threshold ?? 0.2}
                    pulsed={pulsedIds.has(l.id)}
                    {...(isAll
                      ? { searchName: ss?.name ?? `#${l.saved_search_id}` }
                      : {})}
                  />
                );
              })}
              {visibleListings.length === 0 && (
                <tr>
                  <td
                    colSpan={isAll ? 11 : 10}
                    className="px-3 py-4 text-zinc-500 text-sm"
                  >
                    {listings.length === 0
                      ? "No listings yet — try Scan now."
                      : `No listings match "${statusFilter}".`}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ScanScheduleIndicator({
  lastRun,
  now,
}: {
  lastRun: ScanRun | null;
  now: number;
}) {
  if (!lastRun) {
    return (
      <span className="text-xs text-zinc-500 font-mono">never scanned</span>
    );
  }
  // Anchor "next scan" to the most recent successfully-finished run.
  // The scheduler ticks every SCAN_INTERVAL_MIN minutes regardless of
  // user activity; the countdown drifts at most one tick.
  const finished = lastRun.finished_at ? new Date(lastRun.finished_at).getTime() : null;
  const started = new Date(lastRun.started_at).getTime();
  const lastEnd = finished ?? started;
  const sinceMs = now - lastEnd;
  const nextMs = SCAN_INTERVAL_MIN * 60_000 - sinceMs;
  const ago = humanShort(sinceMs);
  const nextLabel =
    nextMs > 0 ? `next in ${humanShort(nextMs)}` : `due now`;
  return (
    <span className="text-xs text-zinc-500 font-mono whitespace-nowrap">
      last scan {ago} ago · {nextLabel}
    </span>
  );
}

function humanShort(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  if (m < 60) return rs > 0 && m < 10 ? `${m}m${rs}s` : `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h${m % 60}m`;
}

function Spinner() {
  // Inline tailwind-only spinner — saves shipping a Heroicons dep.
  return (
    <span
      className="inline-block w-3 h-3 rounded-full border-2 border-yellow-400 border-t-transparent animate-spin"
      aria-hidden="true"
    />
  );
}

function ScanFeedbackBadge({ feedback }: { feedback: ScanFeedback | null }) {
  if (!feedback) return null;
  if (feedback.kind === "starting") {
    return (
      <span className="flex items-center gap-2 text-sm text-yellow-300 font-mono bg-yellow-500/10 border border-yellow-500/30 rounded px-2 py-1 animate-pulse">
        <Spinner />
        Fetching from eBay…
      </span>
    );
  }
  if (feedback.kind === "fetched") {
    return (
      <span className="text-xs text-zinc-300 font-mono">
        eBay returned {feedback.ebay} • {feedback.endingSoon} ending soon •{" "}
        <span className="text-yellow-400">{feedback.toPrice} to price</span>
      </span>
    );
  }
  if (feedback.kind === "finished") {
    if (feedback.live === 0) {
      return (
        <span className="text-xs text-zinc-400 font-mono">
          No PSA-titled listings ending soon
        </span>
      );
    }
    return (
      <span
        className={`text-xs font-mono ${
          feedback.status === "ok" ? "text-green-400" : "text-red-400"
        }`}
      >
        Scan {feedback.status} — {feedback.repriced}/{feedback.live} priced
      </span>
    );
  }
  if (feedback.kind === "skipped") {
    return (
      <span className="text-xs text-zinc-500 font-mono">
        Scan already in progress
      </span>
    );
  }
  return null;
}

function ScanProgress({ listings }: { listings: LiveListing[] }) {
  // Show progress only while a scan is actually in flight. A listing
  // counts as "in flight" only if status='pricing' AND it was last
  // seen by a scan in the last ~3 minutes. Without that freshness
  // gate, a single listing that crashed mid-pricing in a previous
  // scan would pin the indicator at "1 in flight" forever.
  const cutoffFresh = Date.now() - 3 * 60_000;
  const pricing = listings.filter(
    (l) =>
      l.status === "pricing" &&
      new Date(l.last_seen_at).getTime() >= cutoffFresh,
  ).length;
  if (pricing === 0) return null;

  // Scope the totals to the in-flight scan only — listings whose status
  // is `pricing` plus everything that just transitioned out of pricing
  // in the last few seconds. Using last_priced_at within the past 60s
  // approximates "this scan run".
  const cutoff = Date.now() - 60_000;
  const recentlyPriced = listings.filter(
    (l) => l.last_priced_at !== null && new Date(l.last_priced_at).getTime() >= cutoff,
  );
  const total = pricing + recentlyPriced.length;
  const scanned = recentlyPriced.length;
  const pct = total > 0 ? Math.round((scanned / total) * 100) : 0;

  const priced = recentlyPriced.filter((l) => l.status === "priced").length;
  const noComps = recentlyPriced.filter((l) => l.status === "no_comps").length;

  return (
    <div className="ml-auto flex items-center gap-3 font-mono text-xs">
      <div className="flex items-center gap-2">
        <div className="w-32 h-1.5 bg-zinc-800 rounded overflow-hidden">
          <div
            className="h-full bg-yellow-500 transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="text-zinc-300">
          {scanned}/{total} priced
        </span>
      </div>
      {priced > 0 && <span className="text-green-400">{priced} match</span>}
      {noComps > 0 && <span className="text-zinc-500">{noComps} no_comps</span>}
      <span className="text-yellow-400 animate-pulse">{pricing} in flight</span>
    </div>
  );
}
